import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { XCircle, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { getIncidents } from '../services/api';
import { useScrollToEvent } from '../hooks/useScrollToEvent';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select } from '../components/ui/select';
import { EmptyState } from '../components/ui/empty-state';

function IncidentsPage() {
  const { workerId } = useParams();
  const [incidents, setIncidents] = useState([]);
  useScrollToEvent(incidents);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    loadIncidents();
  }, [severityFilter, workerId]);

  const loadIncidents = async () => {
    try {
      setLoading(true);
      const severity = severityFilter === 'all' ? null : severityFilter;
      const response = await getIncidents(severity, 100, workerId);
      setIncidents(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load incidents:', err);
      setError('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity) => {
    const cls = 'h-5 w-5';
    switch (severity) {
      case 'critical':
        return <XCircle className={`${cls} text-destructive`} />;
      case 'high':
        return <AlertTriangle className={`${cls} text-warning`} />;
      case 'medium':
        return <Info className={`${cls} text-primary`} />;
      case 'low':
        return <CheckCircle2 className={`${cls} text-success`} />;
      default:
        return <Info className={`${cls} text-muted-foreground`} />;
    }
  };

  const getSeverityVariant = (severity) => {
    switch (severity) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'warning';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'success';
      default:
        return 'outline';
    }
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Security Incidents"
        description="Monitor and analyze security incidents"
        actions={
          <div className="w-[200px]">
            <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
              <option value="all">All severities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Incidents</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : incidents.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="No incidents"
              description="No incidents found. System is operating normally."
            />
          ) : (
            <ul className="divide-y divide-border">
              {incidents.map((incident) => (
                <li
                  key={incident.id}
                  id={`event-${incident.id}`}
                  className="flex items-start gap-3 p-4"
                >
                  <div className="pt-0.5">{getSeverityIcon(incident.severity)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {incident.incident_type}
                      </span>
                      <Badge variant={getSeverityVariant(incident.severity)}>
                        {incident.severity.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">Risk: {incident.total_risk_score}</Badge>
                    </div>
                    <div className="mt-1 text-sm text-foreground">{incident.description}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {dayjs(incident.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                    </div>
                    {incident.related_screenshot_id && (
                      <div className="text-xs text-primary">
                        Related Screenshot ID: {incident.related_screenshot_id}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default IncidentsPage;
